import { useMemo, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  Users,
  Sprout,
  MessageSquare,
  Brain,
  Crown,
  Library,
  Mic2,
  Zap,
  Target,
  Tags,
  Heart,
  BarChart3,
  CheckCircle,
  Award,
  UserPlus,
  Sparkles,
  SquareStack,
  type LucideIcon,
} from 'lucide-react';
import { TelegramIcon } from '../components/settings/TwoFactorMethodIcons';

export type HomeFeatureFooter = 'xp' | 'boxes' | 'live' | 'telegram';
export type HomeFeatureIcon = ComponentType<{ className?: string; strokeWidth?: number }>;

export type { HomePageStats };

export interface HomePageStats {
  activeLearners: number;
  totalUsers: number;
  totalLessons: number;
  lessonsCompleted: number;
  quizAttempts: number;
  averageQuizScore: number;
  successRate: number;
  libraryBooks: number;
  contentTags: number;
}

type GradientStops = [string, string, string];

export function useHomePageContent(isDark: boolean) {
  const { t } = useTranslation('home');

  const learningJourneySteps = useMemo(
    () => [
      {
        level: t('journey.step1Level'),
        description: t('journey.step1Description'),
        eyebrow: t('journey.step1Eyebrow'),
        gradientLight: ['#ecfdf5', '#a7f3d0', '#6ee7b7'] as GradientStops,
        gradientDark: ['#064e3b', '#047857', '#065f46'] as GradientStops,
        iconWrapClass: 'border-emerald-700/35 bg-emerald-600/15 dark:border-emerald-300/40 dark:bg-emerald-400/10',
        iconClass: 'text-emerald-900 dark:text-emerald-100',
        Icon: Sprout,
      },
      {
        level: t('journey.step2Level'),
        description: t('journey.step2Description'),
        eyebrow: t('journey.step2Eyebrow'),
        gradientLight: ['#eff6ff', '#bfdbfe', '#93c5fd'] as GradientStops,
        gradientDark: ['#0c4a6e', '#1d4ed8', '#1e3a8a'] as GradientStops,
        iconWrapClass: 'border-blue-700/35 bg-blue-600/15 dark:border-blue-300/40 dark:bg-blue-400/10',
        iconClass: 'text-blue-900 dark:text-blue-100',
        Icon: MessageSquare,
      },
      {
        level: t('journey.step3Level'),
        description: t('journey.step3Description'),
        eyebrow: t('journey.step3Eyebrow'),
        gradientLight: ['#fffbeb', '#fde68a', '#fcd34d'] as GradientStops,
        gradientDark: ['#422006', '#713f12', '#451a03'] as GradientStops,
        iconWrapClass: 'border-amber-800/35 bg-amber-600/20 dark:border-amber-300/40 dark:bg-amber-400/10',
        iconClass: 'text-amber-950 dark:text-amber-50',
        Icon: Brain,
      },
      {
        level: t('journey.step4Level'),
        description: t('journey.step4Description'),
        eyebrow: t('journey.step4Eyebrow'),
        gradientLight: ['#fff7ed', '#fed7aa', '#fdba74'] as GradientStops,
        gradientDark: ['#7c2d12', '#9a3412', '#431407'] as GradientStops,
        iconWrapClass: 'border-orange-800/40 bg-orange-600/20 dark:border-orange-300/40 dark:bg-orange-400/10',
        iconClass: 'text-orange-950 dark:text-orange-50',
        Icon: Crown,
      },
    ],
    [t]
  );

  const platformFeatures = useMemo(
    () =>
      [
        { key: 'featureInteractiveLessons', Icon: BookOpen, gradientLight: ['#fff7ed', '#fed7aa', '#fdba74'], gradientDark: ['#7c2d12', '#9a3412', '#431407'] },
        { key: 'featureLibrary', Icon: Library, gradientLight: ['#f0fdfa', '#99f6e4', '#5eead4'], gradientDark: ['#134e4a', '#0f766e', '#042f2e'] },
        { key: 'featurePronunciation', Icon: Mic2, gradientLight: ['#f0f9ff', '#bae6fd', '#7dd3fc'], gradientDark: ['#0c4a6e', '#075985', '#0e7490'] },
        { key: 'featureXpPlans', Icon: Zap, gradientLight: ['#fffbeb', '#fde68a', '#fcd34d'], gradientDark: ['#422006', '#713f12', '#451a03'], footer: 'xp' as const },
        { key: 'featureAssessments', Icon: Target, gradientLight: ['#fdf2f8', '#fbcfe8', '#f9a8d4'], gradientDark: ['#500724', '#9d174d', '#831843'], footer: 'xp' as const },
        { key: 'featureTaggedPractice', Icon: Tags, gradientLight: ['#f5f3ff', '#ddd6fe', '#c4b5fd'], gradientDark: ['#4c1d95', '#5b21b6', '#2e1065'] },
        { key: 'featureCommunity', Icon: Heart, gradientLight: ['#fdf4ff', '#f5d0fe', '#e879f9'], gradientDark: ['#701a75', '#86198f', '#4a044e'], footer: 'live' as const, liveLabel: t('insightLive') },
        { key: 'featureProgress', Icon: BarChart3, gradientLight: ['#ecfdf5', '#a7f3d0', '#5eead4'], gradientDark: ['#064e3b', '#047857', '#134e4a'], footer: 'live' as const, liveLabel: t('insightLive') },
        {
          key: 'featureLeitner',
          Icon: SquareStack,
          gradientLight: ['#fff1f2', '#fda4af', '#fb7185'],
          gradientDark: ['#881337', '#9f1239', '#4c0519'],
          footer: 'boxes' as const,
          ribbon: t('featureLeitner.ribbon'),
        },
        {
          key: 'featureTelegram',
          Icon: TelegramIcon,
          gradientLight: ['#e0f2fe', '#7dd3fc', '#0ea5e9'],
          gradientDark: ['#0c4a6e', '#0369a1', '#075985'],
          footer: 'telegram' as const,
          liveLabel: t('featureTelegram.live'),
        },
      ].map((item) => ({
        title: t(`${item.key}.title`),
        description: t(`${item.key}.description`),
        eyebrow: t(`${item.key}.eyebrow`),
        bullets: [t(`${item.key}.bullet1`), t(`${item.key}.bullet2`), t(`${item.key}.bullet3`)],
        gradientLight: item.gradientLight as GradientStops,
        gradientDark: item.gradientDark as GradientStops,
        Icon: item.Icon as HomeFeatureIcon,
        footer: 'footer' in item ? item.footer : undefined,
        ribbon: 'ribbon' in item ? item.ribbon : undefined,
        liveLabel: 'liveLabel' in item ? item.liveLabel : undefined,
      })),
    [t]
  );

  const featureShowcaseSections = useMemo(
    () => [
      {
        title: t('showcaseLearn.title'),
        subtitle: t('showcaseLearn.subtitle'),
        id: 'learn',
        sectionClass: 'bg-gradient-to-b from-amber-50/90 via-orange-50/50 to-amber-50/70 dark:from-amber-950/20 dark:via-gray-900 dark:to-gray-900',
        cardVariant: 'panel' as const,
        featureIndexes: [0, 2],
      },
      {
        title: t('showcaseRead.title'),
        subtitle: t('showcaseRead.subtitle'),
        id: 'read',
        sectionClass: 'bg-gradient-to-b from-sky-50/95 to-cyan-50/60 dark:from-sky-950/25 dark:to-gray-950',
        cardVariant: 'discovery' as const,
        featureIndexes: [1, 5],
      },
      {
        title: t('showcasePractice.title'),
        subtitle: t('showcasePractice.subtitle'),
        id: 'practice',
        sectionClass: 'bg-gradient-to-b from-violet-50/90 to-fuchsia-50/50 dark:from-violet-950/20 dark:to-gray-950',
        cardVariant: 'quest' as const,
        featureIndexes: [4, 3, 8],
      },
      {
        title: t('showcaseTrack.title'),
        subtitle: t('showcaseTrack.subtitle'),
        id: 'track',
        sectionClass: 'bg-gradient-to-b from-emerald-50/90 to-teal-50/55 dark:from-emerald-950/20 dark:to-gray-950',
        cardVariant: 'insight' as const,
        featureIndexes: [7, 6, 9],
      },
    ],
    [t]
  );

  const primaryStats = useMemo(
    (): {
      key: keyof HomePageStats;
      label: string;
      detail: string;
      format: (s: HomePageStats) => string;
      accentBarClass: string;
      watermarkIconClass: string;
      Icon: LucideIcon;
    }[] => [
      {
        key: 'activeLearners',
        label: t('stats.activeLearners'),
        detail: t('stats.activeLearnersDetail'),
        format: (s) => formatNumber(s.activeLearners),
        accentBarClass: 'bg-sky-500',
        watermarkIconClass: 'text-sky-600',
        Icon: Users,
      },
      {
        key: 'totalLessons',
        label: t('stats.lessonsAvailable'),
        detail: t('stats.lessonsAvailableDetail'),
        format: (s) => formatNumber(s.totalLessons),
        accentBarClass: 'bg-emerald-500',
        watermarkIconClass: 'text-emerald-600',
        Icon: BookOpen,
      },
      {
        key: 'lessonsCompleted',
        label: t('stats.lessonsCompleted'),
        detail: t('stats.lessonsCompletedDetail'),
        format: (s) => formatNumber(s.lessonsCompleted),
        accentBarClass: 'bg-violet-500',
        watermarkIconClass: 'text-violet-600',
        Icon: CheckCircle,
      },
      {
        key: 'averageQuizScore',
        label: t('stats.averageScore'),
        detail: t('stats.averageScoreDetail'),
        format: (s) => `${Math.round(s.averageQuizScore)}%`,
        accentBarClass: 'bg-amber-500',
        watermarkIconClass: 'text-amber-600',
        Icon: Award,
      },
    ],
    [t]
  );

  const secondaryStats = useMemo(
    () => [
      { key: 'totalUsers' as const, label: t('stats.totalMembers'), format: (s: HomePageStats) => formatNumber(s.totalUsers), Icon: UserPlus },
      { key: 'quizAttempts' as const, label: t('stats.quizAttempts'), format: (s: HomePageStats) => formatNumber(s.quizAttempts), Icon: Target },
      { key: 'libraryBooks' as const, label: t('stats.libraryBooks'), format: (s: HomePageStats) => formatNumber(s.libraryBooks ?? 0), Icon: Library },
      { key: 'contentTags' as const, label: t('stats.practiceTags'), format: (s: HomePageStats) => formatNumber(s.contentTags ?? 0), Icon: Tags },
      { key: 'successRate' as const, label: t('stats.successRate'), format: (s: HomePageStats) => `${s.successRate}%`, Icon: Sparkles },
    ],
    [t]
  );

  return {
    learningJourneySteps,
    platformFeatures,
    featureShowcaseSections,
    primaryStats,
    secondaryStats,
    isDark,
  };
}

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M+`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K+`;
  return num.toString();
}
